// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DefaultChartPropsOnChangeValue.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import {
  isEqual,
  filter,
  get,
  isNil,
  isEmpty,
  includes,
  getOr,
  omitBy,
  memoize,
  find,
  forEach,
  omit,
  flatMap,
  flow,
  has,
  constant
} from 'lodash/fp';

import { action } from 'mobx';

export default (reportPartDetails, fieldStore) =>
  action(
    'OnChangeChartProps',
    (
      schemaData: any,
      changedKey: string,
      changedKeyPath: any,
      changedValue: any,
      changedOthersInfo: any,
      derivedChange: any
    ) => {
      const reportPartContent = reportPartDetails.reportPartContent || {};

      const groupData = changedKeyPath && changedKeyPath.length ? get(changedKeyPath, schemaData) : schemaData;
      const fieldData = groupData[changedKey];

      fieldData && (fieldData.value = changedValue);

      const generalInfo = getOr({}, 'generalInfo', schemaData);
      const chart = getOr({}, 'chart', schemaData);
      const labels = getOr({}, 'labels', schemaData);
      const legends = getOr({}, 'legends', schemaData);
      const grouping = getOr({}, 'grouping', schemaData);
      const data = getOr({}, 'data', schemaData);
      const view = getOr({}, 'view', schemaData);
      const printing = getOr({}, 'printing', schemaData);
      const dataRefreshInterval = getValue('dataRefreshInterval', view);
      const chartType = getValue('chartType', generalInfo);
      const vizEngine = getValue('library', generalInfo);
      const izMultiColor = getMultiColorValue(changedKey, chartType, chart);
      const izMultiLevel = getValue('izMultiLevel', chart);

      const izBorder = getValue('izBorder', chart) || {};
      const izXYPlane = getValue('izXYplane', chart);
      const colorTheme = getValue('colorTheme', chart);

      const izGridline = getValue('izGridline', chart) || {};
      const izBackGroundColor = getValue('izBackgroundColor', chart) || {};
      const legendSettings = getValue('legendSettings', legends) || {};

      const thresholdSettings = getValue('izThresholds', data) || {};
      const izBottomXpercent = getValue('izBottomXpercent', grouping) || 0;
      const izPageBreakAfterSeparator = getValue('izPageBreakAfterSeparator', printing) || false;

      const chartProperties = {
        chartType: chartType,
        colorTheme: colorTheme.colorPalette,
        isDefaultTheme: colorTheme.isDefaultTheme,
        commonOptions: {
          /* border */
          'izEntire.borderColor': izBorder['izEntire.borderColor'],
          'izEntire.borderWidth': izBorder['izEntire.borderWidth'],
          'izEntire.borderStyle': izBorder['izEntire.borderStyle'] /* label orientation for data */,
          izLabelOrientation: getValue('izLabelOrientation', labels),
          izHoverLabels: getValue('izHoverLabels', labels) /*background color */,
          'izEntire.backgroundColor': izBackGroundColor['izEntire.backgroundColor'],
          'izPlot.backgroundColor': izBackGroundColor['izPlot.backgroundColor'] /*Legend settings */,
          'izLegend.visibility': legendSettings['izLegend.visibility'],
          'izLegend.horizontalAlign': legendSettings['izLegend.horizontalAlign'],
          'izLegend.verticalAlign': legendSettings['izLegend.verticalAlign'],
          'izLegend.borderColor': legendSettings['izLegend.borderColor'],
          'izLegend.borderWidth': legendSettings['izLegend.borderWidth'],
          'izLegend.fontStyling': legendSettings['izLegend.fontStyling'],
          'izLegend.backgroundColor': legendSettings['izLegend.backgroundColor'],
          'izLegend.borderVisible': legendSettings['izLegend.borderVisible']
        },
        optionByType: {
          /* Multi Color */
          izMultiColor: izMultiColor,
          izMultiLevel: izMultiLevel,
          izUseSeparator: getValue('izUseSeparator', grouping),
          izInverted: getValue('izInverted', view),
          izStep: getValue('izStep', view),
          izSpline: getValue('izSpline', view),
          izStacking: getValue('izStacking', grouping),
          izRange: getValue('izRange', data),
          izValueLabel: getValue('izValueLabel', data),
          izSliceLabel: getValue('izSliceLabel', data),
          izShowValueFieldName: getValue('izShowValueFieldName', data),
          izShowPercentage:
            changedKey === 'izBottomXpercent' && izBottomXpercent ? true : getValue('izShowPercentage', data),
          izPieChartStyle: getValue('izPieChartStyle', view),
          izBottomXpercent: izBottomXpercent,
          izZoom: getValue('izZoom', view),
          izUsePagination: getValue('izUsePagination', view),
          izItemPerRow: getValue('izItemPerRow', view),
          izSingleYAxis: getValue('izSingleYAxis', view)
        },
        commonXYAxis: {
          izGridLine: izGridline,
          izLabelOrientationY: getValue('izLabelOrientationY', labels),
          izLabelOrientationX: getValue('izLabelOrientationX', labels),
          metricAllOption: get('metricAllOption', izXYPlane),
          metricAllPlotLinesOption: get('metricAllPlotLinesOption', thresholdSettings),
          allDynamicThresholdLine: get('allDynamicThresholdLine', thresholdSettings)
        },
        view: { dataRefreshInterval: dataRefreshInterval },
        printing: { izPageBreakAfterSeparator: izPageBreakAfterSeparator }
      };

      if (changedKey === 'izThresholds') {
        fieldStore.needUpdatePreview(reportPartDetails, null, null, null, null, null, true);
      }
      if (changedKey === 'chartType') {
        reportPartContent.setChartType(chartProperties.chartType).then(isNeedUpdatePreviewData => {
          isNeedUpdatePreviewData &&
            !isEmpty(isNeedUpdatePreviewData) &&
            fieldStore.needUpdatePreview &&
            fieldStore.needUpdatePreview(reportPartDetails);
        });

        let newMultiColor = getMultiColorValue(changedKey, chartType, chart);
        // update the value for multi color option on the Properties Panel
        if (has('izMultiColor.value', chart)) {
          chart.izMultiColor.value = newMultiColor;
        }

        // set value for report part content to make the chart changing
        reportPartContent.setMultiColor(newMultiColor);

        // Reset range value when change chart type
        if (!isEmpty(getValue('izRange', data))) {
          data.izRange.value = '';
        }
      }

      changedKey === 'izUseSeparator' && reportPartContent.resetSeparator(getValue('izUseSeparator', grouping));
      changedKey === 'izMultiColor' && reportPartContent.setMultiColorDirty();
      changedKey === 'library' && reportPartContent.setVizEngine(vizEngine);

      // if user change stage of multi level. We also reset drilldown filters and cross filters .
      if (changedKey === 'izMultiLevel') {
        if (reportPartContent.expandedLevel > 0) {
          reportPartContent.resetDrilldownInfo && reportPartContent.resetDrilldownInfo();
          fieldStore.removePillByReportPart && fieldStore.removePillByReportPart(reportPartDetails);
        }

        // if user turn on MultiLevel then Multi color also turn on.
        if (!!izMultiLevel) {
          if (has('izMultiColor.value', chart)) {
            chart.izMultiColor.value = true;
          }

          if (has('optionByType.izMultiColor', chartProperties)) {
            chartProperties.optionByType.izMultiColor = true;
          }
          reportPartContent.setMultiColor(true);
          reportPartContent.setMultiColorDirty();
        }
      }

      derivedChange && derivedChange(chartProperties);

      // Remove properties which don't have for selected chart-type
      chartProperties.optionByType = omitBy(isNil, chartProperties.optionByType);
      // Commit the change to model
      changedKey !== 'chartType' && reportPartDetails.setReportPartProperties(chartProperties);

      const reloadData =  includes(changedKey, ['izRange', 'izMultiLevel']);

      let resultPromise = needToResetFunctionFormat()
        ? fieldStore.resetFunctionFormat(reportPartContent).then(constant(true))
        : Promise.resolve(reloadData);

      return resultPromise.then(needToUpdate => ({
        needToUpdate,
        chartProperties
      }));

      function needToResetFunctionFormat() {
        return (
          changedKey === 'izRange' &&
          has('izRange', reportPartContent.defaultOptionsByType) &&
          get('optionByType.izRange', chartProperties) === ''
        );
      }

      /**
       * Get value multi color option
       * @param {string} changedKey
       * @param {string} chartType
       * @param {Object} obj
       */
      function getMultiColorValue(changedKey: string, chartType: string, chart: any) {
        const defaultChartOptionByType = reportPartContent.defaultOptionsByType;
        const izMultiColorDefaultValue =
          has('izMultiColor', defaultChartOptionByType) && defaultChartOptionByType.izMultiColor;

        let izMultiColor = null;
        if (changedKey === 'chartType' && reportPartDetails.state === 1) {
          izMultiColor = reportPartContent.multiColorDirty ? getValue('izMultiColor', chart) : izMultiColorDefaultValue;
        } else {
          izMultiColor = getValue('izMultiColor', chart);
        }
        return izMultiColor;
      }
    }
  );

/**
 * Get value
 * @param {string} key
 * @param {Object} obj
 */
function getValue(key: string, obj: any) {
  return get([key, 'value'].join('.'), obj);
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.DefaultChartPropsOnChangeValue.js